import React, { useEffect, useRef, useState } from "react";

/* region Imports - Libraries */
import { TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import Yup from "@libraries/yup";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
/* endregion Imports - Libraries */
/* region Imports - GraphQL */
import { useLazyQuery, useMutation } from "@apollo/client";
import { paramAllWithPagination, TParamAllWithPaginationQueries } from "@graphql/groups/param/query.graphql";
import {
  EWhereClausesOperators,
  ParamMutationsUpdateArgs,
  ParamQueriesGetAllWithPaginationArgs
} from "@graphql/helpers/types.graphql";
import { paramUpdateMutation, TParamUpdateMutations } from "@graphql/groups/param/mutation.graphql";
/* endregion Imports - GraphQL */
/* region Imports - Atoms */
import Loading from "@atoms/Loading";
/* endregion Imports - Atoms */
/* region Imports - Constants */
import { EParamsId } from "src/constants/enum/EParamsId";
/* endregion Imports - Constants */
/* region Imports - Language */
import { GlobalMessages, SettingMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
/* endregion Imports - Language */
/* region Imports - Styles */
import Styled from "./styles";
/* endregion Imports - Styles */

// region Interfaces

interface IConfigurableParams {
  minDaysForSolicitation: number;
}

interface IConfigurableParamsFormData {
  minDaysForSolicitation: number;
}
// endregion Interfaces

const FormEnvParams: React.FC = () => {

  /* region States */
  const [configurableParams, setConfigurableParams] = useState<IConfigurableParams>({
    minDaysForSolicitation: 1
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /* endregion States */
  /* region Hooks */
  const { addToast } = useToast();
  const configurableParamsRef = useRef<IConfigurableParams>({} as IConfigurableParams);
  const { t } = useTranslation();
  /* endregion Hooks */
  /* region Form validation */
  const paramsSchema = Yup.object().shape({
    minDaysForSolicitation: Yup.number()
  });

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<IConfigurableParamsFormData>({
    resolver: yupResolver(paramsSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });
  /* endregion Form validation */
  /* region GraphQL */
  const [getParams] = useLazyQuery<
    TParamAllWithPaginationQueries,
    ParamQueriesGetAllWithPaginationArgs
  >(paramAllWithPagination(["id", "name", "key"]), {
    fetchPolicy: "network-only"
  });

  const [updateParamMutation] = useMutation<
    TParamUpdateMutations,
    ParamMutationsUpdateArgs
  >(
    paramUpdateMutation(["id", "name", "key"])
  );

  /* endregion GraphQL */
  /* region Functions */

  /*
    * Update, if necessary, the params in database
   */
  const FsaveParams = async () => {

    try {

      setIsLoading(true);

      const hasUpdated = Object.keys(configurableParamsRef.current).some(
        (key) => configurableParamsRef.current[key] !== configurableParams[key]
      );

      if (!hasUpdated) {
        addToast({
          title: t(GlobalMessages.warningText),
          description: t(SettingMessages.settingParamsNoChange),
          type: "info"
        });

        return;
      }

      const { data } = await updateParamMutation({
        variables: {
          param: {
            id: EParamsId.PROGRAMATION_SOLICITATION_MIN_DAYS,
            key: configurableParams.minDaysForSolicitation.toString()
          }
        }
      });

      if (data?.param.update.status === "SUCCESS") {
        configurableParamsRef.current = {
          ...configurableParams
        };

        addToast(
          {
            title: `${t(GlobalMessages.successText)}!`,
            description: t(SettingMessages.settingParamsSuccessUpdate),
            type: "success"
          }
        );
      } else {
        addToast({
          title: t(GlobalMessages.errorText),
          description: data?.param.update.message,
          type: "error"
        });
      }

    } catch (error) {
      addToast(
        {
          title: `${t(GlobalMessages.errorText)}!`,
          description: error.message,
          type: "error"
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * Get the min days of programation solicitation from database
   */
  const FgetParams = async () => {

    setIsLoading(true);

    try {
      const { data } = await getParams({
        variables: {
          where: [
            {
              property: "id",
              operator: EWhereClausesOperators.IN,
              value: EParamsId.PROGRAMATION_SOLICITATION_MIN_DAYS
            }
          ]
        }
      });

      if (data?.param.getAllWithPagination.status === "SUCCESS") {

        const result = data?.param.getAllWithPagination.result;

        const minDaysParam = Number(
          result?.find((item) => item?.id === EParamsId.PROGRAMATION_SOLICITATION_MIN_DAYS)?.key
        );

        setConfigurableParams((prevState) => ({
          ...prevState,
          minDaysForSolicitation: Number(minDaysParam)
        }));

        configurableParamsRef.current = {
          ...configurableParamsRef.current,
          minDaysForSolicitation: Number(minDaysParam)
        };
      }
    } catch (error) {
      addToast(
        {
          title: `${t(GlobalMessages.errorText)}!`,
          description: error.message,
          type: "error"
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  /* endregion Functions */
  /* region useEffects */

  useEffect(() => {

    FgetParams().then();
  }, [getParams]);
  /* endregion useEffects */

  return (
    <Styled.ParamsContainer>
      <Loading loading={isLoading} />
      {!isLoading && (
        <form onSubmit={handleSubmit(FsaveParams)}>
          <Styled.ParamItem>
            <Styled.ParamItemText>{t(SettingMessages.settingParamsMinDaysDescription)}</Styled.ParamItemText>
            <Controller
              name="minDaysForSolicitation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t(SettingMessages.settingParamsDaysLabel)}
                  type="number"
                  className="numberInput"
                  variant="standard"
                  placeholder="1"
                  value={configurableParams.minDaysForSolicitation}
                  onChange={(e) => setConfigurableParams((prevState) => ({
                    ...prevState,
                    minDaysForSolicitation: Number(e.target.value)
                  }))}
                  error={!!formErrors.minDaysForSolicitation}
                />
              )}
            />
          </Styled.ParamItem>
          <Styled.SubmitButton className="confirmButton" type="submit" disabled={isLoading}>
            Alterar
          </Styled.SubmitButton>
        </form>
      )}
    </Styled.ParamsContainer>
  );
};

export default FormEnvParams;
